/**
 * SettingsContext.js — Backend-driven user settings
 *
 * Loads the user's settings (`/api/Setting/GetUserSettings`) after login and
 * exposes them by key. The backend is the source of truth; localStorage keeps
 * a per-key bootstrap cache (`fms.setting.<key>`) so consumers like the theme
 * and map style can apply the last known value before the first paint.
 *
 * Writes are optimistic: `setSettingValue` updates the React Query cache and
 * the localStorage mirror immediately, then calls EditUserSetting.
 *
 * Usage:
 *   const { getSetting, getSettingValue, setSettingValue } = useSettings();
 *   setSettingValue(SETTING_KEYS.MAP_MODE, 3);
 */

import React, { createContext, useContext, useCallback, useEffect, useMemo } from 'react';
import { fleetApi } from '../api/fleetApi';
import { useAuth } from './AuthContext';
import { useUserSettingsQuery } from '../hooks/queries/useUserSettingsQuery';
import { useEditUserSetting } from '../hooks/queries/useSettingMutations';

// Well-known setting keys seeded in the backend DefaultSetting table.
export const SETTING_KEYS = {
  MAP_MODE:     'MapMode',
  SYSTEM_COLOR: 'SystemColor',
};

const cacheKey = (key) => `fms.setting.${key}`;

/** Last known raw value for a key — readable before login/API completes. */
export const readCachedSettingValue = (key) => {
  try { return window.localStorage.getItem(cacheKey(key)); } catch { return null; }
};

const writeCachedSettingValue = (key, value) => {
  try { window.localStorage.setItem(cacheKey(key), String(value)); } catch {
    // storage unavailable (private mode / quota) — cache just won't persist
  }
};

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const { currentUser, authToken } = useAuth();
  const userId = currentUser?.id || null;

  const { data: settings = [], isLoading, isError, refetch } = useUserSettingsQuery(fleetApi, userId, authToken);
  const editSetting = useEditUserSetting(fleetApi, userId, authToken);

  // Mirror server values into the bootstrap cache whenever they load/change.
  useEffect(() => {
    settings.forEach((s) => {
      if (s.key && s.rawValue != null) writeCachedSettingValue(s.key, s.rawValue);
    });
  }, [settings]);

  const getSetting = useCallback(
    (key) => settings.find((s) => s.key === key) || null,
    [settings]
  );

  const getSettingValue = useCallback(
    (key) => getSetting(key)?.value ?? null,
    [getSetting]
  );

  const setSettingValue = useCallback(
    (key, value) => {
      const setting = settings.find((s) => s.key === key);
      if (!setting) return; // no user row for this key — backend materializes rows from defaults
      writeCachedSettingValue(key, value);
      editSetting.mutate({ id: setting.id, value });
    },
    [settings, editSetting]
  );

  const value = useMemo(
    () => ({
      settings,
      isLoading,
      isError,
      refetch,
      isSaving: editSetting.isPending,
      getSetting,
      getSettingValue,
      setSettingValue,
    }),
    [settings, isLoading, isError, refetch, editSetting.isPending, getSetting, getSettingValue, setSettingValue]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
